import React from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, applyTheme } from '../../contexts/AuthContext';
import { useTranslation } from '../../contexts/LanguageContext';
import ThemeToggle from '../Shared/ThemeToggle';

function NavIcon({ name }) {
  switch (name) {
    case 'tools':
      return (
        <svg className="nav-icon" viewBox="0 0 16 16" fill="none">
          <rect x="1" y="1" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
          <rect x="9" y="1" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
          <rect x="1" y="9" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
          <rect x="9" y="9" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
        </svg>
      );
    case 'graph':
      return (
        <svg className="nav-icon" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.4" />
          <circle cx="2.5" cy="3" r="1.5" stroke="currentColor" strokeWidth="1.3" />
          <circle cx="13.5" cy="3" r="1.5" stroke="currentColor" strokeWidth="1.3" />
          <circle cx="2.5" cy="13" r="1.5" stroke="currentColor" strokeWidth="1.3" />
          <circle cx="13.5" cy="13" r="1.5" stroke="currentColor" strokeWidth="1.3" />
          <line x1="4" y1="4" x2="6.5" y2="6.5" stroke="currentColor" strokeWidth="1.2" />
          <line x1="12" y1="4" x2="9.5" y2="6.5" stroke="currentColor" strokeWidth="1.2" />
          <line x1="4" y1="12" x2="6.5" y2="9.5" stroke="currentColor" strokeWidth="1.2" />
          <line x1="12" y1="12" x2="9.5" y2="9.5" stroke="currentColor" strokeWidth="1.2" />
        </svg>
      );
    case 'admin':
      return (
        <svg className="nav-icon" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.4" />
          <path d="M2 13c0-2.761 2.686-5 6-5s6 2.239 6 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      );
    case 'profile':
    default:
      return (
        <svg className="nav-icon" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="5.5" r="3" stroke="currentColor" strokeWidth="1.4" />
          <path d="M1.5 14c0-3.038 2.91-5.5 6.5-5.5s6.5 2.462 6.5 5.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      );
  }
}

export default function Sidebar() {
  const { profile, userRole, isAdmin, isInternal, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const adminExpanded = isAdmin && location.pathname.startsWith('/admin');

  // Theme aus localStorage auf html-Element halten (falls Profil andere Vorgabe macht)
  React.useEffect(() => {
    if (profile?.profile?.theme) {
      applyTheme(profile.profile.theme);
    }
  }, [profile]);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const displayName = profile?.display_name || profile?.email || '—';
  const roleKey = `sidebar.role.${userRole}`;
  const roleLabel = t(roleKey);
  const avatarInitial = ((profile?.display_name || profile?.email || '?')[0] || '?').toUpperCase();
  const avatarUrl = profile?.profile?.avatar;

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <img
          src="/secaas-logo-white.png"
          alt="SECaaS"
          style={{ height: 22, opacity: .9, flexShrink: 0 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="sidebar-logo-name">{t('sidebar.logo.title')}</div>
          <div className="sidebar-logo-tag">{t('sidebar.logo.tag')}</div>
        </div>
        <ThemeToggle small />
      </div>

      <nav className="sidebar-nav">
        <div className="nav-section-title">{t('nav.section.main')}</div>

        <NavLink to="/" end className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}>
          <NavIcon name="tools" />
          {t('nav.tools')}
        </NavLink>

        {isInternal && (
          <NavLink to="/graph" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}>
            <NavIcon name="graph" />
            {t('nav.graph')}
          </NavLink>
        )}

        {isAdmin && (
          <>
            <NavLink
              to="/admin"
              className={() => 'nav-item' + (adminExpanded ? ' active' : '')}
            >
              <NavIcon name="admin" />
              {t('nav.admin')}
            </NavLink>
            {adminExpanded && (
              <div style={{ paddingLeft: 18, display: 'flex', flexDirection: 'column' }}>
                <NavLink
                  to="/admin/tenants"
                  className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}
                  style={{ fontSize: 12 }}
                >
                  {t('nav.admin.tenants')}
                </NavLink>
                <NavLink
                  to="/admin/admins"
                  className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}
                  style={{ fontSize: 12 }}
                >
                  {t('nav.admin.admins')}
                </NavLink>
                <NavLink
                  to="/admin/permissions"
                  className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}
                  style={{ fontSize: 12 }}
                >
                  {t('nav.admin.permissions')}
                </NavLink>
              </div>
            )}
          </>
        )}

        <NavLink to="/profile" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}>
          <NavIcon name="profile" />
          {t('nav.profile')}
        </NavLink>
      </nav>

      <div className="sidebar-footer">
        <div
          className="sidebar-user"
          onClick={() => navigate('/profile')}
          title={t('nav.profile')}
        >
          <div
            className="user-avatar"
            style={avatarUrl ? { backgroundImage: `url('${avatarUrl}')` } : {}}
          >
            {!avatarUrl && avatarInitial}
          </div>
          <div className="user-info">
            <div className="user-name">{displayName}</div>
            <div className="user-role">{roleLabel}</div>
          </div>
        </div>
        <button
          type="button"
          className="btn btn-ghost"
          style={{ width: '100%', fontSize: 12 }}
          onClick={handleLogout}
        >
          {t('nav.logout')}
        </button>
      </div>
    </aside>
  );
}
